import {
  EarningsTableRowData,
  StakingPeriod,
  StakingPhaseData,
  StakingPhaseData_V2,
  UserDeposit,
  VipPhaseData,
} from '../types';

/**
 * StakeTable item type (matches component expectation)
 * Note: Uses 'date' field name as per StakeTable component
 */
export type StakeTableItem = {
  name: string;
  apy: string;
  poolSize: number;
  leftInThePool: number;
  status: import('../types').StakingTableStatus;
  canStake: boolean;
  isFinalCall: boolean;
  date: StakingPhaseData[];
  /** Phase index for identifying phase when staking */
  phaseIndex: number;
  /** Full periods data for staking popup */
  periods: StakingPeriod[];
};

/**
 * Transforms StakingPhaseData_V2 (from context) to StakeTableItem (for StakeTable component)
 */
export const transformPhaseToTableData = (phase: StakingPhaseData_V2): StakeTableItem => {
  // Transform periods to StakingPhaseData format
  const periodData: StakingPhaseData[] = phase.periods.map((period) => ({
    name: period.label,
    days: period.days,
    apy: period.apyDisplay,
    poolSize: parseFloat(period.target) / 1e18, // Convert wei to display
    leftInThePool: period.remainingDisplay,
    canStake: period.canStake,
    isFinalCall: period.isFinalCall,
  }));

  return {
    name: phase.name,
    apy: phase.apyRange,
    poolSize: parseFloat(phase.totalTarget) / 1e18,
    leftInThePool: phase.remainingCapacityDisplay,
    canStake: phase.canStake,
    isFinalCall: false, // Phase level doesn't have final call
    status: phase.status,
    date: periodData, // Note: StakeTable uses 'date' not 'data'
    phaseIndex: phase.phaseIndex,
    periods: phase.periods, // Full periods data for staking popup
  };
};

/**
 * Transforms array of StakingPhaseData_V2 to format expected by StakeTable
 */
export const transformPhasesToTableData = (phases: StakingPhaseData_V2[]): StakeTableItem[] => {
  return phases.map(transformPhaseToTableData);
};

/**
 * Transforms array of VipPhaseData to StakeTableItem[] for StakeTable (VIP variant)
 */
export const transformVipPhasesToTableData = (vipPhases: VipPhaseData[]): StakeTableItem[] => {
  return vipPhases.map(transformPhaseToTableData);
};

/**
 * Transforms UserDeposit (from context) to EarningsTableRowData (for EarningsTable component)
 */
export const transformDepositToTableRow = (deposit: UserDeposit): EarningsTableRowData => {
  // Show withdraw warning for periodical staking (not flexible) when period is still active
  const isFlexible = deposit.periodDays === '0';
  const isPeriodActive = deposit.daysLeft !== null && deposit.daysLeft > 0;
  const showWithdrawInfo = !isFlexible && deposit.canWithdraw && isPeriodActive;

  return {
    id: deposit.id,
    phase: deposit.phaseName,
    status: deposit.status,
    daysLeft: deposit.daysLeft ?? undefined,
    staked: deposit.amount,
    apy: deposit.apy,
    rewards: deposit.rewards,
    canClaim: deposit.canClaim,
    canWithdraw: deposit.canWithdraw,
    showWithdrawInfo,
    isWhitelisted: deposit.isWhitelisted,
    periodLabel: deposit.periodLabel,
    startDate: deposit.stakingStartDate,
    endDate: deposit.stakingEndDate,
    isFlexible,
  };
};

/**
 * Transforms array of UserDeposit to EarningsTableRowData array
 */
export const transformDepositsToTableData = (deposits: UserDeposit[]): EarningsTableRowData[] => {
  return deposits.map(transformDepositToTableRow);
};
